import { useState, useLayoutEffect, useEffect, useRef, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'

/**
 * PortalDropdown — the canonical way to render a floating panel (menu, calendar,
 * autocomplete, tooltip-with-content) so it is NEVER clipped by an ancestor's
 * `overflow: hidden/auto/scroll`.
 *
 * Why this exists: hand-rolled `position:absolute` panels rely on no ancestor
 * happening to clip them. The moment one is placed inside a scrollable modal,
 * card, or table, it gets cropped (see the Ready-to-Send row menu, the
 * record-payment calendar, the reports info tooltip). This renders the panel to
 * `document.body` with `position:fixed`, positioned from the anchor's
 * `getBoundingClientRect()` — exactly the pattern already used by the
 * new-estimate wizard combobox and the opportunities row menu — so ancestor
 * overflow is irrelevant.
 *
 * Reach for this instead of a bespoke absolute-positioned panel.
 */

type Placement = 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end'

interface PortalDropdownProps {
  /** Whether the panel is shown. */
  open: boolean
  /** The trigger/field the panel is positioned against. */
  anchorRef: RefObject<HTMLElement | null>
  /** Called when the user clicks outside the panel/anchor or presses Escape. */
  onClose: () => void
  /** Preferred side + alignment. Auto-flips vertically when there isn't room. */
  placement?: Placement
  /** Gap in px between the anchor and the panel. */
  offset?: number
  /** Force the panel to the anchor's width (for autocompletes/comboboxes). */
  matchAnchorWidth?: boolean
  /** Class applied to the panel — keep its existing visual styles here. */
  className?: string
  children: ReactNode
}

export function PortalDropdown({
  open,
  anchorRef,
  onClose,
  placement = 'bottom-start',
  offset = 6,
  matchAnchorWidth = false,
  className,
  children,
}: PortalDropdownProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number; width?: number } | null>(null)

  useLayoutEffect(() => {
    if (!open) {
      setPos(null)
      return
    }

    function reposition() {
      const anchor = anchorRef.current
      const panel = panelRef.current
      if (!anchor) return
      const a = anchor.getBoundingClientRect()
      const vpW = window.innerWidth
      const vpH = window.innerHeight
      const ph = panel?.offsetHeight ?? 0
      const pw = matchAnchorWidth ? a.width : panel?.offsetWidth ?? 0

      const [side, align] = placement.split('-') as ['top' | 'bottom', 'start' | 'end']

      // Vertical placement with auto-flip when the preferred side lacks room.
      const spaceBelow = vpH - a.bottom
      const spaceAbove = a.top
      let useSide = side
      if (side === 'bottom' && spaceBelow < ph + offset && spaceAbove > spaceBelow) useSide = 'top'
      if (side === 'top' && spaceAbove < ph + offset && spaceBelow > spaceAbove) useSide = 'bottom'
      const top = useSide === 'bottom' ? a.bottom + offset : Math.max(8, a.top - offset - ph)

      // Horizontal alignment, clamped to the viewport.
      let left = align === 'end' ? a.right - pw : a.left
      left = Math.max(8, Math.min(left, vpW - pw - 8))

      setPos({ top, left, width: matchAnchorWidth ? a.width : undefined })
    }

    reposition()
    // capture:true so scrolling in nested scroll containers (modals/cards) also repositions.
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [open, placement, offset, matchAnchorWidth, anchorRef])

  useEffect(() => {
    if (!open) return
    function onPointerDown(e: MouseEvent) {
      const t = e.target as Node
      if (panelRef.current?.contains(t)) return
      if (anchorRef.current?.contains(t)) return
      onClose()
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onClose, anchorRef])

  if (!open) return null

  return createPortal(
    <div
      ref={panelRef}
      className={className}
      style={{
        position: 'fixed',
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        // Neutralize any positioning the panel's own class sets (top/right/bottom).
        right: 'auto',
        bottom: 'auto',
        width: pos?.width,
        visibility: pos ? 'visible' : 'hidden',
        zIndex: 9999,
      }}
    >
      {children}
    </div>,
    document.body,
  )
}
